// MeetingCard — inline card for MessageType.Meeting (9). Shows the huddle /
// scheduled-meeting state (LIVE / scheduled time / ended); Join opens the meeting
// inline (onJoin) or, on a direct link, falls back to the /meet route. State is
// patched live by the MeetingUpdated event (Channels rewrites metadataJson).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { MeetingTopicsModal } from "./MeetingTopics.jsx";

const REPEATS = { daily: "Repeats daily", weekdays: "Repeats every weekday", weekly: "Repeats weekly" };

export default function MeetingCard({ message, onJoin, onDelete, currentUserId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [, tick] = useState(0); // re-render to flip "scheduled" → "started" at the start time
  let meeting = null;
  try {
    meeting = JSON.parse(message.metadataJson || "{}").meeting;
  } catch { /* malformed metadata → render nothing */ }

  const when = meeting?.scheduledAt ? new Date(meeting.scheduledAt) : null;
  // When a scheduled meeting reaches its start, flip the card to "Started" in-app (even before
  // anyone joins / the server marks it live). Schedule one timeout at the start moment.
  useEffect(() => {
    if (!when) return undefined;
    const ms = when.getTime() - Date.now();
    if (ms <= 0 || ms > 24 * 3600 * 1000) return undefined;
    const t = setTimeout(() => tick((x) => x + 1), ms + 500);
    return () => clearTimeout(t);
  }, [when?.getTime()]);
  if (!meeting) return null;

  const ended = meeting.status === 2;
  const live = meeting.status === 1;
  const started = !live && !ended && when && Date.now() >= when.getTime(); // its time has arrived
  const link = `${window.location.origin}/meet/${meeting.code}`;
  const canDelete = onDelete && currentUserId != null && currentUserId === meeting.createdBy;

  const join = () => (onJoin ? onJoin(meeting.code) : navigate(`/meet/${meeting.code}`));

  return (
    <div className={`meetingcard ${ended ? "meetingcard--ended" : ""}`}>
      <div className="meetingcard__head">
        <span className="meetingcard__icon">📞</span>
        <div className="meetingcard__title">
          <strong>{meeting.title}</strong>
          <small>
            {live && <span className="meetingcard__live">● LIVE</span>}
            {live && meeting.participants > 0 &&
              ` · ${meeting.participants} ${meeting.participants === 1 ? "person" : "people"} in the huddle`}
            {started && <span className="meetingcard__live">● Meeting started</span>}
            {!live && !ended && when && !started &&
              `🗓 ${when.toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
            {!live && !ended && when && meeting.durationMinutes > 0 &&
              ` · ${meeting.durationMinutes >= 60 ? `${meeting.durationMinutes / 60} hr` : `${meeting.durationMinutes} min`}`}
            {!live && !ended && !when && "Starting…"}
            {ended && "Ended"}
            {meeting.recurrence && REPEATS[meeting.recurrence] && ` · ${REPEATS[meeting.recurrence]}`}
            {meeting.topicsTotal > 0 && ` · 📋 ${meeting.topicsDone ?? 0}/${meeting.topicsTotal} covered`}
          </small>
        </div>
        {canDelete && (
          <button className="meetingcard__del" title="Remove this card" onClick={() => onDelete(message)}>✕</button>
        )}
      </div>
      <div className="meetingcard__actions">
        {!ended && <button className="btn btn--primary" onClick={join}>Join</button>}
        {!ended && (
          <button className="btn btn--soft" title="Copy meeting link"
            onClick={() => navigator.clipboard?.writeText(link).catch(() => {})}>
            🔗
          </button>
        )}
        {/* Staff prep the agenda from the card before the meeting (in-call it's the 📋 panel). */}
        {!ended && user?.isStaff && (
          <button className="btn btn--soft" title="Discussion topics for this meeting"
            onClick={() => setAgendaOpen(true)}>
            📋 Agenda
          </button>
        )}
      </div>
      {agendaOpen && (
        <MeetingTopicsModal code={meeting.code} title={meeting.title} canEdit={!!user?.isStaff}
          onClose={() => setAgendaOpen(false)} />
      )}
    </div>
  );
}
